<template>
  <div
    v-if="openingHoursToday"
    class="store-current-opening-time"
  >
    <span
      class="scot-tag"
      :class="{
        'scot-tag--closed': isOpen.closed,
        'scot-tag--soon': isOpen.soon,
      }"
    >
      {{ $t(isOpen.display) }}
    </span>

    <template v-if="!storeIsClosed && storeHasBreak && showOpeningTime">
      <div class="scot-right">
        {{
          $t('store.openingHours.time', {
            timespan: formattedOpeningHoursToday + ' - ' + formattedStartPauseTime,
          })
        }}
        <br />
        {{
          $t('store.openingHours.time', {
            timespan: formattedEndPauseTime + ' - ' + formattedClosingHoursToday,
          })
        }}
      </div>
    </template>
    <template v-else-if="!storeIsClosed && !storeHasBreak && todayIsSpecialDay && showOpeningTime">
      {{
        $t('store.openingHours.time', {
          timespan: formattedSpecialDayOpeningHoursToday + ' - ' + formattedSpecialDayClosingHoursToday,
        })
      }}
    </template>
    <template v-else-if="((!storeIsClosed && !storeHasBreak) || todayOpen) && showOpeningTime">
      {{ ' ' + openingTimeToday }}
    </template>
  </div>
</template>

<script lang="ts">
export default defineComponent({
  props: {
    store: {
      type: Object,
      required: true,
    },
    showOpeningTime: {
      type: Boolean,
      default: true,
    },
    showTodayOpeningTimeWithWeekDay: {
      type: Boolean,
    },
    weekDay: {
      type: String,
    },
  },
  computed: {
    openingHoursToday() {
      if (!this.store || (this.store && !this.store.openingHours)) {
        return null;
      }

      if (this.store.openingHours.weekDayOpeningList.length <= 0) {
        return null;
      }

      const currentDate = new Date();

      // set current day to yesterday to fix divergence Array from BE and week-structure in JS
      currentDate.setDate(currentDate.getDate() - 1);

      const dayOfWeek = currentDate.getDay();

      return this.store.openingHours.weekDayOpeningList[dayOfWeek];
    },

    todayIsSpecialDay() {
      const specialDayOpeningList = this.store.openingHours?.specialDayOpeningList;
      const found = specialDayOpeningList?.find((specialDay: any) => {
        const todaysDate = new Date();
        const specialDate = new Date(specialDay.date);
        // check for the date and equalize hours, min, sec, ms for comparison
        return specialDate?.setHours(0, 0, 0, 0) === todaysDate.setHours(0, 0, 0, 0);
      });

      return found;
    },

    isOpen(): boolean | { display: string; closed?: boolean; soon?: boolean } {
      if (!this.store) {
        return false;
      }
      switch (this.store.status) {
        case 'CLOSED_SOON':
          return {
            display: 'store.openingHours.closedSoon',
            soon: true,
          };
        case 'CLOSED':
          if (this.todayOpen) {
            return {
              display: 'store.openingHours.closedUntilShort',
              closed: true,
            };
          }
          return {
            display: 'store.openingHours.closedToday',
            closed: true,
          };
        case 'BREAK':
          return {
            display: 'store.openingHours.openSoon',
            soon: true,
          };
        case 'OPENED_SOON':
          return {
            display: 'store.openingHours.openSoon',
            soon: true,
          };
        case 'OPENED':
          return {
            display: 'store.openingHours.openToday',
            closed: false,
          };
        default:
          return {
            display: 'store.openingHours.openToday',
            closed: false,
          };
      }
    },

    formattedOpeningHoursToday() {
      return this.openingHoursToday.openingTime.formattedHour;
    },

    formattedClosingHoursToday() {
      return this.openingHoursToday.closingTime.formattedHour;
    },

    formattedStartPauseTime() {
      return this.openingHoursToday.startPauseTime.formattedHour;
    },

    formattedEndPauseTime() {
      return this.openingHoursToday.endPauseTime.formattedHour;
    },

    formattedSpecialDayOpeningHoursToday() {
      return this.todayIsSpecialDay?.openingTime.formattedHour;
    },

    formattedSpecialDayClosingHoursToday() {
      return this.todayIsSpecialDay?.closingTime.formattedHour;
    },

    storeIsClosed() {
      return this.store.status === 'CLOSED';
    },

    todayOpen() {
      if (this.openingHoursToday.closed) {
        return false;
      }

      const { hour } = this.openingHoursToday.openingTime;
      const { minute } = this.openingHoursToday.openingTime;

      const openingTimeToday = new Date(`${new Date().toDateString()} ${hour}:${minute}`).getTime();

      if (!this.openingHoursToday.closed && this.store.status !== 'opened-soon' && Date.now() < openingTimeToday) {
        return true;
      }

      return false;
    },

    storeHasBreak() {
      return this.store.status === 'BREAK';
    },

    openingTimeToday() {
      const timespan = `${this.formattedOpeningHoursToday} - ${this.formattedClosingHoursToday}`;

      if (!this.showTodayOpeningTimeWithWeekDay) {
        return this.$t('store.openingHours.time', {
          timespan,
        });
      }
      return `${this.weekDay} ${this.$t('store.openingHours.time', {
        timespan,
      })}`;
    },
  },
});
</script>

<style lang="scss" scoped>
.store-current-opening-time {
  margin-bottom: $pad;

  &:last-child {
    margin-bottom: 0;
  }

  .scot-tag {
    color: var(--color-general-text-status-positive-default);
    font-weight: bold;

    &.scot-tag--closed {
      color: $color-general-text-status-negative-default;
    }

    &.scot-tag--soon {
      color: $color-general-text-status-medium-default;
    }
  }

  .scot-right {
    text-align: right;
    display: inline-flex;
  }
}
</style>
